import classNames from 'classnames'
import React from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface BoxedCheckboxOption {
    value: string
    label: string
    description?: string
}

interface BoxedCheckboxGroupControlProps<T extends FieldValues> {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, any>
    options: BoxedCheckboxOption[]
    label?: string
    description?: string
    isDisabled?: boolean
    onChangeCustom?: (value: string[]) => void
}

export function BoxedCheckboxGroupControl<T extends FieldValues>({
    name,
    control,
    rules,
    options,
    label,
    description,
    isDisabled,
    onChangeCustom
}: BoxedCheckboxGroupControlProps<T>): React.ReactElement {
    const { t } = useTranslation()
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
                const selectedValues = (
                    Array.isArray(field.value) ? field.value : []
                ) as string[]

                const handleToggle = (value: string) => {
                    if (isDisabled) {
                        return
                    }

                    const newValues = selectedValues.includes(value)
                        ? selectedValues.filter((v) => v !== value)
                        : [...selectedValues, value]

                    field.onChange(newValues)
                    if (onChangeCustom) {
                        onChangeCustom(newValues)
                    }
                }

                return (
                    <div className="w-full space-y-4 relative">
                        {(label || description) && (
                            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm py-4 mb-2 border-b border-gray-100 shadow-sm">
                                {label && (
                                    <label className="block text-lg font-medium text-gray-700">
                                        {t(label)}
                                    </label>
                                )}
                                {description && (
                                    <p className="text-sm text-gray-500 mt-2 font-normal">
                                        {t(description)}
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="space-y-3">
                            {options.map((option, index) => {
                                const isSelected = selectedValues.includes(
                                    option.value
                                )
                                const letter = letters[index % letters.length]

                                return (
                                    <div
                                        key={option.value}
                                        onClick={() =>
                                            handleToggle(option.value)
                                        }
                                        style={{
                                            backgroundColor: isSelected
                                                ? 'rgba(0, 191, 104, 0.1)'
                                                : undefined,
                                            borderColor: isSelected
                                                ? '#00BF68'
                                                : undefined
                                        }}
                                        className={classNames(
                                            'relative flex items-center p-3 cursor-pointer border rounded-lg transition-all duration-200 group',
                                            {
                                                'ring-1 ring-[#00BF68]':
                                                    isSelected,
                                                'border-gray-200 hover:border-[#00BF68] hover:bg-gray-50':
                                                    !isSelected && !isDisabled,
                                                'opacity-50 cursor-not-allowed':
                                                    isDisabled
                                            }
                                        )}
                                    >
                                        {/* Letter Box */}
                                        <div
                                            className={classNames(
                                                'flex items-center justify-center w-6 h-6 rounded border text-xs font-bold mr-3 transition-colors',
                                                {
                                                    'bg-[#00BF68] text-white border-[#00BF68]':
                                                        isSelected,
                                                    'bg-white text-gray-500 border-gray-300 group-hover:border-[#00BF68] group-hover:text-[#00BF68]':
                                                        !isSelected &&
                                                        !isDisabled
                                                }
                                            )}
                                        >
                                            {letter}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1">
                                            <div
                                                className={classNames(
                                                    'font-medium text-sm',
                                                    {
                                                        'text-[#00BF68]':
                                                            isSelected,
                                                        'text-gray-700':
                                                            !isSelected
                                                    }
                                                )}
                                            >
                                                {option.label}
                                            </div>
                                            {option.description && (
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    {option.description}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        {error && (
                            <p className="text-danger text-xs mt-1">
                                {error.message}
                            </p>
                        )}
                    </div>
                )
            }}
        />
    )
}
